import * as pinia from 'pinia';
import { computed } from 'vue';
import { useLocalStorage } from '@vueuse/core';
import ChatService from '@/Services/ChatService';
import useSessionStore from '@/Stores/useSessionStore';
import { phasesData } from '@/Utils/configs';
import { USER_ROLE } from '@/Utils/vice-constants';

const sessionStore = useSessionStore();
const userType = computed(() => {
  if (sessionStore.userIsObserver) return USER_ROLE.OBSERVER;
  if (sessionStore.isClinician) {
    return USER_ROLE.CLINICIAN;
  }
  return USER_ROLE.PARTICIPANT;
});

const useChatMessagesStore = pinia.defineStore('ChatMessagesStore', {
  state: () => ({
    unreadCount: 0,
    chatMessages: [],
    oldMessages: useLocalStorage('_vice:oldMessages', {}),
  }),

  getters: {
    allChatMessages: (state) => state.chatMessages,
    hasUnreadMessages: (state) => state.unreadCount > 0,
  },

  actions: {
    addChatMessage(messagePayload) {
      // Jitsi echoes a sent command back to its own sender via MUC presence, and sendChatMessage
      // already added the message locally the moment it was sent - so the echo of our own
      // message arrives here carrying the exact same client-generated id. Dedup by id (rather
      // than by sender identity) since sender ids aren't guaranteed unique across roles.
      const alreadyExists = this.allChatMessages.some(
        (message) => message.id === messagePayload.id,
      );
      if (alreadyExists) {
        return;
      }

      this.allChatMessages.push(messagePayload);

      const sessionId = sessionStore.sessionUniqueId;
      const messageId = messagePayload.id;

      if (!this.oldMessages[sessionId]) {
        this.oldMessages[sessionId] = [];
      }

      // Check if message is already seen
      const isOldMessage =
        Array.isArray(this.oldMessages[sessionId]) &&
        this.oldMessages[sessionId].includes(messageId);

      // Clinicians and participants/observers are stored in separate tables with their own
      // auto-incrementing ids, so a Clinician.id and a User.id can collide (e.g. both being 1) -
      // "is this my own message" must also check the sender's role, not just id.
      const isOwnMessage =
        messagePayload.sender?.id === sessionStore.authUser?.id &&
        messagePayload.sender?.type === userType.value;

      if (
        !isOwnMessage &&
        !isOldMessage &&
        (!sessionStore.isOpenSidebar || sessionStore.sidebarTab !== 'chatTab')
      ) {
        this.unreadCount += 1;
      }
    },

    clearUnreadCount() {
      this.unreadCount = 0;

      const sessionId = sessionStore.sessionUniqueId;
      if (!this.oldMessages[sessionId]) {
        this.oldMessages[sessionId] = [];
      }

      // Remove other sessions from oldMessages
      this.cleanupOldSessions(sessionId);

      // Add all current message IDs to oldMessages
      this.allChatMessages.forEach((message) => {
        if (
          !(
            Array.isArray(this.oldMessages[sessionId]) &&
            this.oldMessages[sessionId].includes(message.id)
          )
        ) {
          this.oldMessages[sessionId].push(message.id);
        }
      });
    },

    // Clean up old sessions
    cleanupOldSessions(sessionId) {
      Object.keys(this.oldMessages).forEach((key) => {
        if (key !== sessionId) {
          delete this.oldMessages[key];
        }
      });
    },

    async sendChatMessage(chatData) {
      const senderInfo = {
        id: sessionStore.authUser.id,
        name: sessionStore.authUser?.name,
        type: userType.value,
        avatar: sessionStore.authUser?.profile_photo_url,
      };

      const phaseInfo = {
        id: sessionStore.currentPhase,
        type: phasesData?.[sessionStore.currentPhase]?.title,
      };

      const payload = {
        ...chatData,
        sentAt: new Date().toISOString(),
        handledAt: null,
        sender: JSON.stringify(senderInfo),
        phase: JSON.stringify(phaseInfo),
      };

      sessionStore.jitsiCommands.sendViceMessage(payload);

      // Send message to smart glass
      if (chatData.sendTo === USER_ROLE.CLINICIAN && chatData.type === 'message') {
        sessionStore.deviceMessageSender.sendSmartGlassTextMessage({
          ...chatData,
          deviceMessageType: 'MESSAGE',
          phaseInfo,
          senderInfo,
          sentAt: payload.sentAt,
          handledAt: payload.handledAt,
        });
      }

      const messagePayload = {
        ...payload,
        sender: senderInfo,
        phase: phaseInfo,
        attachment: payload.attachment ? JSON.parse(payload.attachment) : null,
      };

      // Jitsi echoes a sent command back to its own sender via MUC presence, so this message
      // would eventually show up via onReceiveViceMessage anyway - but not reliably enough to
      // depend on for our own messages. Add it locally right away instead; onReceiveViceMessage
      // ignores the echo of our own message when it arrives, so this doesn't create a duplicate.
      this.addChatMessage(messagePayload);

      // Persist Data
      try {
        await ChatService.addNewMessage(sessionStore.sessionUniqueId, messagePayload);
      } catch (error) {
        console.error(error);
      }
    },
  },
});

export default useChatMessagesStore;
